import { readManuscriptTasks } from '~shared/manuscriptTasks';
import { manuscriptChangeSuggestionEntries, manuscriptChangeSuggestionTexts, manuscriptChangeUsesCompactResult, parseManuscriptChangeMessageRef, type ManuscriptChangeMessageRef } from "./manuscriptChangeMessageRef";

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : {};
}

export type IndependentPreviewGroup = {
  change: ManuscriptChangeMessageRef;
  changes?: ManuscriptChangeMessageRef[];
  compact?: boolean;
  key: string;
  title: string;
  suggestions: string[];
  explanations?: string[];
  documentSuggestions?: string[][];
  labeledSuggestions?: Array<{ text: string; label: string }>;
};

/** Recover separate preview deliveries from their source results, including older
 * messages whose denormalized carousel merged unrelated option-zero entries. */
export function independentPreviewGroups(executions: unknown, metadata?: unknown): IndependentPreviewGroup[] {
  const sourceGroups = record(metadata).chatRewriteSources;
  const labeledSuggestions = (Array.isArray(sourceGroups) ? sourceGroups : []).flatMap(group => {
    const options = record(record(group).source).options;
    return (Array.isArray(options) ? options : []).flatMap(raw => {
      const option = record(raw);
      return typeof option.prose === "string" && typeof option.label === "string" && option.label.trim()
        ? [{ text: option.prose, label: option.label.trim() }] : [];
    });
  });
  const tasks = readManuscriptTasks(metadata);
  if (tasks !== null) {
    // Canonical task deliveries take precedence over legacy message-wide mirrors.
    // Applying one document retires its actions, not its saved prose or siblings.
    executions = tasks.filter(task => !['declined', 'undone'].includes(String(task.manuscriptChange.status)))
      .map(task => ({ name: 'propose_manuscript_change',
      arguments: { goal_key: task.goalId, goal_revision: task.goalRevision,
        operation: task.operation, display_title: task.displayTitle, replacement_explanations: task.explanations },
      result: { data: task.manuscriptChange },
    }));
  }
  if (!Array.isArray(executions)) return [];
  const groups = new Map<string, IndependentPreviewGroup>();
  const setDocuments = new Map<string, Map<string, string[]>>();
  const setCompact = new Map<string, Map<string, boolean>>();
  const setChanges = new Map<string, Map<string, ManuscriptChangeMessageRef>>();
  for (const raw of executions) {
    const execution = record(raw);
    if (["approve_manuscript_change", "apply_manuscript_change_set", "apply_selected_option", "apply_wire_insert_follow_up"].includes(String(execution.name))) {
      const result = record(execution.result);
      const data = record(result.data);
      const outcome = result.outcome ?? data.outcome;
      const status = result.status ?? data.status;
      const applied = result.mutationApplied === true || outcome === "applied"
        || status === "applied" || status === "partially_applied"
        || (result.success === true && !outcome && !status);
      if (applied && !result.error && result.success !== false && result.mutationApplied !== false) return [];
      continue;
    }
    if (execution.name !== "propose_manuscript_change") continue;
    const result = record(execution.result);
    if (result.success === false) continue;
    const ref = parseManuscriptChangeMessageRef(result.data);
    if (!ref) continue;

    const args = record(execution.arguments);
    const goalKey = typeof args.goal_key === "string" ? args.goal_key.trim() : "";
    const documentId = ref.documentId || String(args.document_id ?? "");
    const key = ref.changeSetId ? `set:${ref.changeSetId}`
      : goalKey ? `${goalKey}:${String(args.goal_revision ?? "")}:${documentId}` : ref.proposalGroupId || ref.changeId;
    let suggestions = manuscriptChangeSuggestionTexts({ manuscriptChange: ref, operation: args.operation });
    let compact = manuscriptChangeUsesCompactResult(ref) || Boolean(ref.preview?.literalReplacements?.length
      && (ref.requestedOptionCount ?? 1) === 1
      && (ref.options ?? []).every(option => option.preview?.literalReplacements?.length));
    if (!suggestions.length && !compact) continue;
    let actionChange: ManuscriptChangeMessageRef = { ...ref, documentId };
    if (ref.changeSetId) {
      // A set may span documents; align its alternatives, without merging separate sets.
      // Replace repeated results for a document rather than duplicating their prose.
      const documents = setDocuments.get(key) ?? new Map<string, string[]>();
      documents.set(documentId || ref.changeId, suggestions);
      setDocuments.set(key, documents);
      const changes = setChanges.get(key) ?? new Map<string, ManuscriptChangeMessageRef>();
      changes.set(documentId || ref.changeId, { ...ref, documentId });
      setChanges.set(key, changes);
      const compactMembers = setCompact.get(key) ?? new Map<string, boolean>();
      compactMembers.set(documentId || ref.changeId, compact);
      setCompact.set(key, compactMembers);
      compact = [...compactMembers.values()].every(Boolean);
      // A literal member must not hide rewrite alternatives or replace their IDs.
      // Pick the member covering the most options, with a stable tie break.
      actionChange = [...changes.values()].sort((a, b) =>
        manuscriptChangeSuggestionEntries({ manuscriptChange: b }).length
        - manuscriptChangeSuggestionEntries({ manuscriptChange: a }).length
        || a.changeId.localeCompare(b.changeId))[0];
      const alternatives = [...documents.values()];
      suggestions = Array.from({ length: Math.max(...alternatives.map(options => options.length)) }, (_, index) =>
        alternatives.flatMap(options => options[index] ? [options[index]] : []).join("\n\n---\n\n"));
    }
    const operation = record(args.operation);
    const description = [args.display_title, operation.instruction, operation.instructions]
      .find(value => typeof value === "string" && value.trim());
    const label = typeof description === "string" ? description.trim().replace(/\s+/g, " ") : "";
    const title = label.length > 160 ? `${label.slice(0, 157).trimEnd()}…` : label;
    groups.set(key, {
      key,
      change: actionChange,
      compact,
      ...(ref.changeSetId ? { changes: [...setChanges.get(key)!.values()] } : {}),
      title: ref.changeSetId && (setDocuments.get(key)?.size ?? 0) > 1
        ? suggestions.length > 1 ? "Combined rewrite alternatives" : "Combined suggested revision"
        : title || (suggestions.length > 1 ? "Rewrite alternatives" : "Suggested revision"),
      suggestions,
      labeledSuggestions: [
        ...labeledSuggestions,
        ...[...(setChanges.get(key)?.values() ?? [actionChange])].flatMap(member =>
          manuscriptChangeSuggestionEntries({ manuscriptChange: member }).flatMap(entry =>
            entry.label ? [{ text: entry.text, label: entry.label }] : [])),
      ],
      ...(Array.isArray(args.replacement_explanations) ? { explanations: args.replacement_explanations.map(value => typeof value === "string" ? value : "") } : {}),
      // Receipts may contain each document's original prose rather than the joined card text.
      ...(ref.changeSetId ? { documentSuggestions: [...setDocuments.get(key)!.values()] } : {}),
    });
  }
  return groups.size > 1 || (tasks?.length ?? 0) > 1 || tasks?.some(task => task.rewriteSource) ? [...groups.values()] : [];
}

/** The cards already show these exact preview bodies. Keep the independent
 * assistant receipt while removing only standalone copies of card prose. */
export function textWithoutPreviewGroupSuggestions(
  markdown: string,
  groups: readonly { suggestions: readonly string[]; documentSuggestions?: readonly (readonly string[])[]; labeledSuggestions?: readonly { text: string; label: string }[] }[],
): string {
  const isOptionHeading = (value: string, optionNumber: number, labels: readonly string[] = []): boolean => {
    const heading = value.trim().replace(/^#{1,6}\s+/, "")
      .replace(/^(?:\*\*|__)(.*?)(?:\*\*|__)$/, "$1");
    return labels.includes(heading) || new RegExp(`^(?:.+?\\s*[:—–-]\\s*)?Option ${optionNumber}(?:\\s*[:—–-](?:\\s+.*)?)?$`, "i").test(heading);
  };
  const paragraphs = markdown.trim().split(/\n\s*\n/);
  // Remove complete options before shorter siblings that share their paragraphs.
  const suggestions = groups.flatMap(group =>
    [group.suggestions, ...(group.documentSuggestions ?? [])].flatMap(options =>
      options.map((suggestion, index) => ({ suggestion, optionNumber: index + 1,
        labels: (group.labeledSuggestions ?? []).filter(item => item.text.trim() === suggestion.trim()).map(item => item.label),
      }))))
    .sort((left, right) => right.suggestion.length - left.suggestion.length);
  for (const { suggestion, optionNumber, labels } of suggestions) {
    const parts = suggestion.trim().split(/\n\s*\n/);
    if (!parts[0]) continue;
    for (let index = paragraphs.length - parts.length; index >= 0; index -= 1) {
      if (index + parts.length > paragraphs.length) continue;
      const first = paragraphs[index].trim();
      const bodyAfterHeading = `\n${parts[0].trim()}`;
      const inlineHeading = first.endsWith(bodyAfterHeading)
        && isOptionHeading(first.slice(0, -bodyAfterHeading.length), optionNumber, labels);
      if (!inlineHeading && first !== parts[0].trim()) continue;
      if (!parts.slice(1).every((part, offset) => paragraphs[index + offset + 1].trim() === part.trim())) continue;
      paragraphs.splice(index, parts.length);
      if (!inlineHeading && index > 0 && isOptionHeading(paragraphs[index - 1], optionNumber, labels)) {
        paragraphs.splice(index - 1, 1);
      }
    }
  }
  return paragraphs.filter(part => part.trim()).join("\n\n");
}

export type PreviewPresentationBlock = { type: 'text'; text: string }
  | { type: 'preview'; group: IndependentPreviewGroup; optionIndex?: number };

/** Resolve persisted ordering through current cards so lifecycle and action IDs
 * come from canonical previews, never from the presentation metadata itself. */
function recoverPreviewOrder(markdown: string, groups: IndependentPreviewGroup[]): PreviewPresentationBlock[] | null {
  const candidates = groups.flatMap(group => group.compact ? [] : group.suggestions.map((prose, optionIndex) => ({ group, prose, optionIndex })));
  // Historical text has no IDs. Only unique, exact, standalone preview bodies
  // establish position; never infer associations from an "Option 1" heading.
  if (!candidates.length || new Set(candidates.map(candidate => candidate.prose)).size !== candidates.length) return null;
  const matches = candidates.map(candidate => {
    const start = markdown.indexOf(candidate.prose);
    const end = start + candidate.prose.length;
    if (start < 0 || markdown.indexOf(candidate.prose, end) !== -1
      || (start > 0 && !markdown.slice(0, start).endsWith('\n\n'))
      || (end < markdown.length && !markdown.slice(end).startsWith('\n\n'))) return null;
    return { ...candidate, start, end };
  });
  if (matches.some(match => !match)) return null;
  const ordered = matches.filter((match): match is NonNullable<typeof match> => match !== null).sort((a, b) => a.start - b.start);
  const blocks: PreviewPresentationBlock[] = [];
  let offset = 0;
  for (const match of ordered) {
    if (match.start < offset) return null;
    let text = markdown.slice(offset, match.start).trim();
    const paragraphs = text.split(/\n\s*\n/);
    const heading = (paragraphs.at(-1) ?? '').replace(/^#{1,6}\s+/, '').replace(/^(?:\*\*|__)(.*?)(?:\*\*|__)$/, '$1');
    const normalize = (value: string) => value.replace(/[—–]/g, '-').trim();
    if (match.group.labeledSuggestions?.some(label => label.text === match.prose && normalize(label.label) === normalize(heading))) {
      paragraphs.pop(); text = paragraphs.join('\n\n');
    }
    if (text) blocks.push({ type: 'text', text });
    blocks.push({ type: 'preview', group: match.group, optionIndex: match.optionIndex });
    offset = match.end;
  }
  if (markdown.slice(offset).trim()) blocks.push({ type: 'text', text: markdown.slice(offset).trim() });
  for (const group of groups.filter(group => group.compact)) blocks.push({ type: 'preview', group });
  return blocks;
}

export function orderedPreviewPresentation(metadata: unknown, groups: IndependentPreviewGroup[], markdown?: string): PreviewPresentationBlock[] | null {
  const presentation = record(record(metadata).previewPresentation);
  if (!Object.prototype.hasOwnProperty.call(record(metadata), 'previewPresentation')) return markdown ? recoverPreviewOrder(markdown, groups) : null;
  if (presentation.version !== 1 || !Array.isArray(presentation.blocks)) return null;
  const blocks: PreviewPresentationBlock[] = [];
  const shown = new Set<string>();
  const key = (group: IndependentPreviewGroup, index: number) => `${group.key}:${index}`;
  for (const raw of presentation.blocks) {
    const block = record(raw);
    if (block.type === 'text' && typeof block.text === 'string') {
      blocks.push({ type: 'text', text: block.text });
      continue;
    }
    if (block.type !== 'preview' || typeof block.changeId !== 'string' || typeof block.documentId !== 'string') return null;
    let match: { group: IndependentPreviewGroup; index: number } | undefined;
    for (const group of groups) {
      for (const member of group.changes ?? [group.change]) {
        const index = group.compact && member.changeId === block.changeId && member.documentId === block.documentId ? 0
          : manuscriptChangeSuggestionEntries({ manuscriptChange: member }).findIndex(entry =>
            entry.optionId === block.changeId && (entry.documentId ?? member.documentId) === block.documentId);
        if (index >= 0) { match = { group, index }; break; }
      }
      if (match) break;
    }
    if (!match) return null;
    const id = key(match.group, match.index);
    // A coordinated card or literal summary may cover several suggestion blocks.
    if (!shown.has(id)) blocks.push({ type: 'preview', group: match.group,
      ...(match.group.compact ? {} : { optionIndex: match.index }) });
    shown.add(id);
  }
  // A tool can prepare an additional preview without repeating it in final prose.
  for (const group of groups) {
    const indices = group.compact ? [0] : group.suggestions.map((_, index) => index);
    for (const index of indices) if (!shown.has(key(group, index))) {
      blocks.push({ type: 'preview', group, ...(group.compact ? {} : { optionIndex: index }) });
    }
  }
  return blocks;
}
